<template>
  <v-row align="center" justify="center" ref="widget">
    <!-- loader -->
    <template v-if="mainLoader">
      <v-progress-circular class="v-progress-circular-fixed" indeterminate color="secondary" />
    </template>
    <!-- end loader -->

    <!-- error -->
    <template v-else-if="error">
      error
    </template>

    <v-col cols="12" :style="`min-width:420px; max-width:700px; background: ${options.backgroundColor.value}`" v-else>

      <v-alert class="mx-7 pb-1 text-center" v-if="instance.test_mode" border="top" type="warning" :icon="false" text dense>
        <span class="body-2 font-weight-medium deep-orange--text text--darken-4">Modo de prueba</span>
      </v-alert>

      <v-stepper v-model="step" alt-labels>
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1" :color="color"><span class="body-2">Plan</span></v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="step > 2" step="2" :color="color"><span class="body-2">Facturación</span></v-stepper-step>
          <v-divider />
          <v-stepper-step step="3" :color="color"><span class="body-2">Pago</span></v-stepper-step>
        </v-stepper-header>
        <v-stepper-items>

          <!-- products -->
          <v-stepper-content step="1" class="pt-0 px-0">

            <!-- frequencies -->
            <v-card class="mx-7 overflow-hidden" v-if="availableFrequencies.length > 1" outlined>
              <v-tabs :color="color" grow height="42" slider-size="3">
                <template v-for="(f, i) in availableFrequencies">
                  <v-tab class="text-none font-weight-medium" :key="`${i}-tab`" @click="activeFrequency=f.id">{{f.name | capitalize}}</v-tab>
                  <v-divider v-if="i < (availableFrequencies.length - 1)" :key="`${i}-divider`" vertical />
                </template>
              </v-tabs>
            </v-card>
            <!-- end frequencies -->

            <!-- plans -->
            <template v-for="(sl, i) in subscription.lines">
              <v-sheet class="overflow-hidden rounded my-4 mx-7" v-if="sl.plan.frequency === activeFrequency" :key="i" outlined>
                <v-checkbox class="px-3 py-0 my-0" v-model="subscription.lines[i].selected" @change="cleanSubscriptionLines(subscription.lines[i])" :color="color" hide-details :readonly="subscription.lines[i].is_mandatory">
                  <template v-slot:label>
                    <v-row class="pl-1" align="center" justify="space-between">
                      <v-col>
                        <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                          {{sl.name}}

                          <!-- plan description -->
                          <v-tooltip max-width="320px" bottom transition="scale-transition">
                            <template v-slot:activator="{on}">
                              <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                            </template>
                            <p-subscription-line-detail :line="sl" :active-currency="activeCurrency" />
                          </v-tooltip>

                          <!-- is mandatory label -->
                          <template v-if="subscription.lines[i].is_mandatory">
                            <v-chip class="ml-1" color="secondary" x-small>Requerido</v-chip>
                          </template>
                        </span>
                        <span class="d-block">
                          <span class="mr-1 body-2"><template v-if="sl.plan.tiers.length">Desde </template>{{sl.plan.amount | currency(activeCurrency.id)}}</span>
                          <span class="mr-1 caption text--primary">{{activeCurrency.id}} por {{sl.plan.product.unit_label}}</span>
                        </span>
                      </v-col>
                      <v-col cols="2" v-if="sl.selected && sl.allow_quantity">
                        <v-btn class="text--primary font-weight-regular" @click.stop="activePlanToQuantityIndex=i; dialogPlanQuantity=true" outlined :ripple="false" small>
                          {{subscription.lines[i].quantity}} {{sl.plan.product.unit_label}}(s)<v-icon right small>mdi-chevron-down</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col lg="4" sm="5" class="text-right">
                        <span class="body-2 text--secondary font-weight-medium">{{sl.subtotal | currency(activeCurrency.id)}}</span>
                        <span class="ml-1 caption text--primary">{{activeCurrency.id}} / {{sl.plan.frequency | frequency}}</span>
                      </v-col>
                    </v-row>
                  </template>
                </v-checkbox>
              </v-sheet>
              <span class="d-block mt-n2 px-11 red--text body-2" v-if="!$v.subscription.lines.$each[i].quantity.minValue" :key="`${i}-plan-error`">La cantidad mínima para suscribir este plan es {{sl.min_quantity}}</span>
            </template>
            <!-- end plans -->

            <!-- dialog plan quantity -->
            <v-dialog v-model="dialogPlanQuantity" no-click-animation :overlay-opacity="0" persistent scrollable width="520">
              <v-card>
                <v-card-title class="pa-0">
                  <v-toolbar flat height="55">
                    <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Cantidad del plan</v-toolbar-title>
                    <v-spacer />
                    <v-toolbar-items>
                      <v-row align="center" justify="center" class="mr-1">
                        <v-btn class="ml-3" @click="dialogPlanQuantity=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
                      </v-row>
                    </v-toolbar-items>
                  </v-toolbar>
                </v-card-title>
                <v-divider />
                <v-card-text class="py-2 px-4">
                  <v-row align="center" justify="center">
                    <v-btn @click="subscription.lines[activePlanToQuantityIndex].quantity -= 1" :disabled="!subscription.lines[activePlanToQuantityIndex].quantity || subscription.lines[activePlanToQuantityIndex].quantity === 1" :color="color" dark fab :ripple="false" small><v-icon dark size="22">mdi-minus</v-icon></v-btn>
                    <v-col cols="5">
                      <v-text-field class="my-0 text-center" v-model.number="$v.subscription.lines.$each[activePlanToQuantityIndex].quantity.$model" :error="$v.subscription.lines.$each[activePlanToQuantityIndex].quantity.$error" @click.stop outlined single-line dense background-color="white" hide-details />
                    </v-col>
                    <v-btn @click="subscription.lines[activePlanToQuantityIndex].quantity += 1" :color="color" dark fab :ripple="false" small><v-icon dark size="22">mdi-plus</v-icon></v-btn>
                  </v-row>
                  <v-row align="center" justify="center">
                    <v-col cols="6" class="pb-0">
                      <span class="d-block body-2 font-weight-medium">{{subscription.lines[activePlanToQuantityIndex].name}}</span>
                    </v-col>
                    <v-col cols="6" class="pb-0 text-right">
                      <span class="d-block caption font-weight-regular" v-if="subscription.lines[activePlanToQuantityIndex].plan.tiers.length">Precio por {{subscription.lines[activePlanToQuantityIndex].plan.product.unit_label}}</span>
                      <span class="d-block caption font-weight-regular" v-else>
                        <span class="text--secondary">{{subscription.lines[activePlanToQuantityIndex].plan.amount | currency(activeCurrency.id)}}</span>
                        <span class="ml-1 text--primary">{{activeCurrency.id}} por {{subscription.lines[activePlanToQuantityIndex].plan.product.unit_label}}</span>
                      </span>
                    </v-col>
                    <v-col cols="12">

                      <!-- tiered plan -->
                      <template v-if="subscription.lines[activePlanToQuantityIndex].plan.tiers.length">
                        <v-list class="mt-2" v-for="(d, i) in subscription.lines[activePlanToQuantityIndex].plan.tiers" :key="i" :color="activeTierIndex === i ? 'background' : ''" rounded>
                          <v-list-item>
                            <v-list-item-title>
                              <span class="body-2 text--secondary font-weight-regular">De {{d.quantity_from}} <template v-if="d.quantity_to">a {{d.quantity_to}}</template><template v-else>o más</template></span>
                              <span class="ml-1 caption">{{subscription.lines[activePlanToQuantityIndex].plan.product.unit_label}}(s)</span>
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-right">
                              <span class="body-2 text--secondary font-weight-medium">{{d.amount | currency(activeCurrency.id)}}</span>
                              <span class="ml-1 text--primary">{{activeCurrency.id}}</span>
                            </v-list-item-subtitle>
                          </v-list-item>
                        </v-list>
                      </template>
                      <!-- end tiered plan -->

                      <!-- total plan -->
                      <v-list class="mt-2" color="background" rounded>
                        <v-list-item>
                          <v-list-item-title class="body-2 text--secondary font-weight-medium">Total</v-list-item-title>
                          <v-list-item-subtitle class="text-right">
                            <span class="body-2 text--secondary font-weight-medium">{{subscription.lines[activePlanToQuantityIndex].subtotal | currency(activeCurrency.id)}}</span>
                            <span class="ml-1 text--primary">{{activeCurrency.id}} /</span>
                            <span class="ml-1 text--primary">{{subscription.lines[activePlanToQuantityIndex].plan.frequency | frequency}}</span>
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                      <!-- end total plan -->

                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions class="pa-4">
                  <v-spacer />
                  <v-btn @click="dialogPlanQuantity=false" :color="color" dark :ripple="false">OK</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- end dialog plan quantity -->

            <!-- addons -->
            <v-row v-if="instance.available_addons.length">
              <v-col class="px-6 pt-6" cols="12">
                <v-toolbar color="transparent" flat height="30">
                  <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Complementos de la suscripción <v-chip v-if="!formHasMandatoryAddons" class="ml-3" dark small label :color="color">Opcional</v-chip></v-toolbar-title>
                </v-toolbar>
              </v-col>
              <v-col cols="12">
                <v-sheet class="overflow-hidden rounded my-4 mx-7" v-for="(sa, i) in subscription.addons" :key="i" outlined>
                  <v-checkbox class="px-3 py-0 my-0" v-model="subscription.addons[i].selected" :color="color" hide-details :readonly="subscription.addons[i].is_mandatory">
                    <template v-slot:label>
                      <v-row class="pl-1" align="center" justify="space-between">
                        <v-col class="text-truncate">
                          <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                            {{sa.name}}

                            <!-- addon description -->
                            <v-tooltip max-width="320px" bottom transition="scale-transition">
                              <template v-slot:activator="{on}">
                                <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                              </template>
                              <span class="d-block text--primary">{{sa.description}}</span>
                            </v-tooltip>

                            <!-- is mandatory label -->
                            <template v-if="subscription.addons[i].is_mandatory">
                              <v-chip class="ml-1" color="secondary" x-small>Requerido</v-chip>
                            </template>
                          </span>
                          <span class="d-block">
                            <span class="body-2">{{sa.addon.amount | currency(activeCurrency.id)}}</span>
                            <span class="ml-1 caption text--primary">{{activeCurrency.id}}</span>
                            <span class="ml-1 caption text--primary">
                              <template v-if="sa.addon.apply_scheme === 'one_time'">el primer {{activeFrequency | frequency('unit')}}</template>
                              <template v-else>{{activeFrequency | frequency}} por {{sa.addon.duration_times}} {{activeFrequency | frequency('plural')}}</template>
                            </span>
                          </span>
                        </v-col>
                        <v-col cols="2" v-if="sa.selected && sa.allow_quantity">
                          <v-btn class="text--primary font-weight-regular" @click.stop="activeAddonToQuantityIndex=i; dialogAddonQuantity=true" outlined :ripple="false" small>
                            {{sa.quantity}}<v-icon right small>mdi-chevron-down</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col lg="4" sm="5" class="text-right">
                          <span class="body-2 text--secondary font-weight-medium">{{sa.addon.amount * sa.quantity | currency(activeCurrency.id)}}</span>
                          <span class="ml-1 caption text--primary">{{activeCurrency.id}}</span>
                        </v-col>
                      </v-row>
                    </template>
                  </v-checkbox>
                </v-sheet>
              </v-col>

              <!-- dialog addon quantity -->
              <v-dialog v-model="dialogAddonQuantity" no-click-animation :overlay-opacity="0" persistent scrollable width="520">
                <v-card>
                  <v-card-title class="pa-0">
                    <v-toolbar flat height="55">
                      <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Cantidad del complemento</v-toolbar-title>
                      <v-spacer />
                      <v-toolbar-items>
                        <v-row align="center" justify="center" class="mr-1">
                          <v-btn class="ml-3" @click="dialogAddonQuantity=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
                        </v-row>
                      </v-toolbar-items>
                    </v-toolbar>
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="py-2 px-4">
                    <v-row align="center" justify="center">
                      <v-btn @click="subscription.addons[activeAddonToQuantityIndex].quantity -= 1" :disabled="!subscription.addons[activeAddonToQuantityIndex].quantity || subscription.addons[activeAddonToQuantityIndex].quantity === 1" :color="color" dark fab :ripple="false" small><v-icon dark size="22">mdi-minus</v-icon></v-btn>
                      <v-col cols="5">
                        <v-text-field class="my-0 text-center" v-model.number="$v.subscription.addons.$each[activeAddonToQuantityIndex].quantity.$model" :error="$v.subscription.addons.$each[activeAddonToQuantityIndex].quantity.$error" @click.stop outlined single-line dense background-color="white" hide-details />
                      </v-col>
                      <v-btn @click="subscription.addons[activeAddonToQuantityIndex].quantity += 1" :color="color" dark fab :ripple="false" small><v-icon dark size="22">mdi-plus</v-icon></v-btn>
                    </v-row>
                    <v-row align="center" justify="center">
                      <v-col cols="12">
                        <span class="d-block body-2 font-weight-medium">{{subscription.addons[activeAddonToQuantityIndex].name}}</span>

                        <!-- total addon -->
                        <v-list class="mt-2" color="background" rounded>
                          <v-list-item>
                            <v-list-item-title class="body-2 text--secondary font-weight-medium">Total</v-list-item-title>
                            <v-list-item-subtitle class="text-right">
                              <span class="body-2 text--secondary font-weight-medium">{{subscription.addons[activeAddonToQuantityIndex].addon.amount * subscription.addons[activeAddonToQuantityIndex].quantity | currency(activeCurrency.id)}}</span>
                              <span class="ml-1 text--primary">{{activeCurrency.id}}</span>
                            </v-list-item-subtitle>
                          </v-list-item>
                        </v-list>
                        <!-- end total addon -->

                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions class="pa-4">
                    <v-spacer />
                    <v-btn @click="dialogAddonQuantity=false" :color="color" dark :ripple="false">OK</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <!-- end dialog plan quantity -->
            </v-row>
            <!-- end addons -->

            <v-row align="center" justify="space-between" class="py-2 px-10">
              <span class="deep-orange--text" v-if="!$v.subscription.lines.minLength"><v-icon color="deep-orange darken-2" left>mdi-alert</v-icon>Selecciona un plan para continuar.</span>
              <v-spacer />
              <v-btn @click="toCustomerStep" :color="color" dark height="38" :ripple="false">Facturación<v-icon right small>mdi-arrow-right</v-icon></v-btn>
            </v-row>
          </v-stepper-content>
          <!-- end products -->

          <!-- billing -->
          <v-stepper-content step="2" class="pt-0 px-0">
            <v-list class="pb-6 transparent">
              <v-list-item>
                <v-list-item-content class="py-0">
                  <v-col cols="12">
                    <v-text-field v-model.trim="$v.signup.customer.name.$model" :label="customLabels.name ? customLabels.name : 'Nombre'" :error="$v.signup.customer.name.$error" :color="color" background-color="white" required persistent-hint dense outlined maxlength="100" autocomplete="new-password" hide-details />
                  </v-col>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content class="py-0">
                  <v-col cols="12">
                    <v-text-field id="email" v-model.trim="$v.signup.customer.email.$model" label="Email de facturación" :error="$v.signup.customer.email.$error" :color="color" persistent-hint dense outlined background-color="white" maxlength="64" hide-details />
                  </v-col>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content class="py-0">
                  <v-col cols="12">
                    <v-text-field v-model.trim="$v.signup.customer.address.$model" label="Dirección" :error="$v.signup.customer.address.$error" :color="color" background-color="white" required persistent-hint dense outlined maxlength="64" hide-details />
                  </v-col>
                  <v-col cols="6">
                    <v-autocomplete v-model="$v.signup.customer.country.$model" :error="$v.signup.customer.country.$error" label="País" :items="instance.available_countries" item-value="id" item-text="name" :item-color="color" :color="color" :menu-props="{ overflowY: true, transition: 'slide-y-transition' }" height="30" background-color="white" autocomplete="new-password" persistent-hint dense outlined hide-details />
                  </v-col>
                  <v-col cols="6">
                    <v-autocomplete v-model="$v.signup.customer.state.$model" :error="$v.signup.customer.state.$error" :label="customLabels.state ? customLabels.state : 'Estado'" :items="statesList" item-value="id" item-text="name" :item-color="color" :color="color" :menu-props="{ overflowY: true, transition: 'slide-y-transition', maxHeight: '180' }" height="30" background-color="white" autocomplete="new-password" persistent-hint dense outlined :no-data-text="signup.customer.country ? 'Sin estados disponibles para el país.' : 'Selecciona primero tu país.'" hide-details />
                  </v-col>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content class="py-0">
                  <v-col cols="6">
                    <v-select v-model="$v.subscription.document.$model" label="Tipo de factura" :error="$v.subscription.document.$error" :items="itemsDocumentSelect" item-value="document.id" item-text="document.name" :item-color="color" :color="color" :menu-props="{ overflowY: true, transition: 'slide-y-transition' }" persistent-hint dense outlined height="30" background-color="white" :no-data-text="signup.customer.country ? 'Sin documentos disponibles para seleccionar.' : 'Selecciona primero un país.'" autocomplete="new-password" hide-details />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model.trim="$v.signup.customer.tax_id.$model" :label="customLabels.tax_id ? customLabels.tax_id : 'ID Fiscal'" :error="$v.signup.customer.tax_id.$error" :color="color" v-dni="signup.customer.country" persistent-hint dense outlined background-color="white" autocomplete="new-password" hide-details>
                      <template v-slot:append>
                        <v-tooltip max-width="280px" transition="scale-transition" left>
                          <template v-slot:activator="{on}">
                            <v-icon class="py-1" v-on="on" small color="secondary">mdi-information</v-icon>
                          </template>
                          <span>También llamado RUT, VAT o Tax ID</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="signup.customer.country === 'CL'">
                <v-list-item-content class="py-0">
                  <v-col cols="12">
                    <v-text-field v-model.trim="$v.signup.customer.tax_settings.cl_activity_description.$model" label="Actividad económica o giro" :error="$v.signup.customer.tax_settings.cl_activity_description.$error" :color="color" persistent-hint dense outlined background-color="white" maxlength="64" hide-details>
                      <template v-slot:append>
                        <v-tooltip max-width="280px" transition="scale-transition" left>
                          <template v-slot:activator="{on}">
                            <v-icon class="py-1" v-on="on" small color="secondary">mdi-information</v-icon>
                          </template>
                          <span>
                            <span class="d-block my-1"><span class="text--secondary font-weight-medium">Si eres persona jurídica</span> escribe la descripción de actividad económica o giro que tienes registrado en el SII.</span>
                            <span class="d-block my-1"><span class="text--secondary font-weight-medium">Si eres persona natural</span> escribe "Sin giro comercial".</span>
                          </span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-row align="center" justify="space-between" class="py-2 px-10">
              <v-btn v-if="displayReturn" @click="step=1" height="38" outlined :ripple="false"><v-icon left small>mdi-arrow-left</v-icon>Plan</v-btn>
              <v-spacer />
              <v-btn @click="toPaymentStep" :color="color" dark height="38" :ripple="false">Pago<v-icon right small>mdi-arrow-right</v-icon></v-btn>
            </v-row>
          </v-stepper-content>
          <!-- end billing -->

          <!-- payment -->
          <v-stepper-content step="3" class="pt-0 px-0">
            <v-row class="py-3 px-6" v-if="subscription.lines.length">
              <v-col cols="12" v-if="error">
                <v-alert class="mb-3 body-2" text dense border="left" type="error" icon="mdi-alert">{{error}}</v-alert>
              </v-col>

              <v-col cols="12" v-if="instantError">
                <v-alert class="mb-3 body-2" text dense border="left" type="error" icon="mdi-alert">{{instantError}}</v-alert>
              </v-col>
              <v-col cols="12">

                <!-- checkout detail -->
                <v-card @click="dialogCheckoutDetail=true" outlined rounded :ripple="false">
                  <v-card-text class="py-2">
                    <v-row align="center" no-gutters v-for="(t, i) in aggregations.totals" :key="`total-${i}`">
                      <v-col cols="7" class="py-1">
                        <!-- billing cycle range -->
                        <span class="text--secondary body-2 font-weight-medium" v-if="aggregations.totals.length > 1">
                          <template v-if="t.endBillingCycle === 1">El primer {{activeFrequency | frequency('unit')}}</template>
                          <template v-else-if="t.endBillingCycle && t.startBillingCycle === t.endBillingCycle">Desde el {{activeFrequency | frequency('unit')}} {{t.endBillingCycle}}</template>
                          <template v-else-if="t.startBillingCycle < t.endBillingCycle">Desde el {{activeFrequency | frequency('unit')}} {{t.startBillingCycle}} al {{t.endBillingCycle}}</template>
                          <template v-else>Después</template>
                          pagarás
                        </span>
                        <!-- end billing cycle range -->

                        <span class="ml-1 text--secondary body-2 font-weight-medium" v-else>Total</span>
                      </v-col>
                      <v-col cols="5" class="py-1 text-right">
                        <span class="text--secondary body-2 font-weight-medium">{{t.total | currency(activeCurrency.id)}}</span>
                        <span class="ml-1 text--primary body-2 font-weight-regular">{{activeCurrency.id}} / {{activeFrequency | frequency}}</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-dialog v-model="dialogCheckoutDetail" no-click-animation :overlay-opacity="0" scrollable width="580">
                  <v-card>
                    <v-card-title class="pa-0">
                      <v-toolbar flat height="55">
                        <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Detalle de la suscripción</v-toolbar-title>
                        <v-spacer />
                        <v-toolbar-items>
                          <v-row align="center" justify="center" class="mr-1">
                            <v-btn class="ml-3" @click="dialogCheckoutDetail=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
                          </v-row>
                        </v-toolbar-items>
                      </v-toolbar>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="py-2 px-4">
                      <v-row align="center" no-gutters v-for="(t, i) in aggregations.totals" :key="`total-${i}`">
                        <v-col cols="7" class="py-1">
                          <!-- billing cycle range -->
                          <span class="text--secondary body-2 font-weight-medium" v-if="aggregations.totals.length > 1">
                            <template v-if="t.endBillingCycle === 1">El primer {{activeFrequency | frequency('unit')}}</template>
                            <template v-else-if="t.endBillingCycle && t.startBillingCycle === t.endBillingCycle">Desde el {{activeFrequency | frequency('unit')}} {{t.endBillingCycle}}</template>
                            <template v-else-if="t.startBillingCycle < t.endBillingCycle">Desde el {{activeFrequency | frequency('unit')}} {{t.startBillingCycle}} al {{t.endBillingCycle}}</template>
                            <template v-else>Después</template>
                            pagarás
                          </span>
                          <!-- end billing cycle range -->

                          <span class="ml-1 text--secondary body-2 font-weight-medium" v-else>Total</span>
                        </v-col>
                        <v-col cols="5" class="py-1 text-right">
                          <span class="d-block">
                            <span class="text--secondary body-2 font-weight-medium">{{t.total | currency(activeCurrency.id)}}</span>
                            <span class="ml-1 text--primary body-2 font-weight-regular">{{activeCurrency.id}} / {{activeFrequency | frequency}}</span>
                          </span>

                          <v-tooltip max-width="320" min-width="180" bottom transition="scale-transition">
                            <template v-slot:activator="{on}">
                              <v-btn v-on="on" :color="color" :ripple="false" small text>Ver detalle</v-btn>
                            </template>
                            <v-list class="pa-0 ma-0" dense>
                              <template v-if="t.exempt">
                                <v-list-item class="px-0">
                                  <v-list-item-title class="body-2 text--secondary font-weight-medium">Neto</v-list-item-title>
                                  <v-list-item-subtitle class="text-right">
                                    <span class="text--secondary font-weight-medium">{{t.amount | currency(activeCurrency.id)}}</span>
                                    <span class="ml-1 caption text--primary font-weight-regular">{{activeCurrency.id}}</span>
                                  </v-list-item-subtitle>
                                </v-list-item>
                                <v-list-item class="px-0">
                                  <v-list-item-title class="body-2 text--secondary font-weight-medium">Exento</v-list-item-title>
                                  <v-list-item-subtitle class="text-right">
                                    <span class="text--secondary font-weight-medium">{{t.exempt | currency(activeCurrency.id)}}</span>
                                    <span class="ml-1 caption text--primary font-weight-regular">{{activeCurrency.id}}</span>
                                  </v-list-item-subtitle>
                                </v-list-item>
                                <v-divider />
                              </template>
                              <v-list-item class="px-0">
                                <v-list-item-title class="body-2 text--secondary font-weight-medium">Subtotal</v-list-item-title>
                                <v-list-item-subtitle class="text-right">
                                  <span class="text--secondary font-weight-medium">{{t.subtotal | currency(activeCurrency.id)}}</span>
                                  <span class="ml-1 caption text--primary font-weight-regular">{{activeCurrency.id}}</span>
                                </v-list-item-subtitle>
                              </v-list-item>
                              <v-list-item class="px-0">
                                <v-list-item-title class="body-2 text--secondary font-weight-medium">Impuestos</v-list-item-title>
                                <v-list-item-subtitle class="text-right">
                                  <span class="text--secondary font-weight-medium">{{t.tax | currency(activeCurrency.id)}}</span>
                                  <span class="ml-1 caption text--primary font-weight-regular">{{activeCurrency.id}}</span>
                                </v-list-item-subtitle>
                              </v-list-item>
                              <v-list-item class="px-0">
                                <v-list-item-title class="body-2 text--secondary font-weight-medium">Total</v-list-item-title>
                                <v-list-item-subtitle class="text-right">
                                  <span class="text--secondary font-weight-medium">{{t.total | currency(activeCurrency.id)}}</span>
                                  <span class="ml-1 caption text--primary font-weight-regular">{{activeCurrency.id}}</span>
                                </v-list-item-subtitle>
                              </v-list-item>
                            </v-list>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                      <v-divider />
                      <v-row>
                        <v-col class="pt-6 pb-0 px-0 ml-n1" cols="12">
                          <v-toolbar color="transparent" flat height="16">
                            <v-toolbar-title class="text--secondary body-2 font-weight-medium">Planes</v-toolbar-title>
                          </v-toolbar>
                        </v-col>
                      </v-row>
                      <v-row align="center" justify="space-between" v-for="sl in subscription.lines.filter(sl => sl.selected)" :key="sl.id">
                        <v-col>
                          <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                            {{sl.name}}
                            <v-tooltip max-width="320px" bottom transition="scale-transition">
                              <template v-slot:activator="{on}">
                                <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                              </template>
                              <p-subscription-line-detail :line="sl" :active-currency="activeCurrency" />
                            </v-tooltip>
                          </span>
                          <span class="d-block">
                            <span class="mr-1 body-2"><template v-if="sl.plan.tiers.length">Desde </template>{{sl.plan.amount | currency(activeCurrency.id)}}</span>
                            <span class="mr-1 caption text--primary">{{activeCurrency.id}} por {{sl.plan.product.unit_label}}</span>
                          </span>
                        </v-col>
                        <v-col cols="2" class="text-right" v-if="sl.allow_quantity">
                          <span class="caption text--secondary">Cant. {{sl.quantity | number}}</span>
                        </v-col>
                        <v-col lg="4" sm="5" class="text-right">
                          <span class="body-2 text--secondary font-weight-medium">{{sl.subtotal | currency(activeCurrency.id)}}</span>
                          <span class="ml-1 caption text--primary">{{activeCurrency.id}} / {{sl.plan.frequency | frequency}}</span>
                        </v-col>
                      </v-row>
                      <template v-if="subscription.addons.filter(sa => sa.selected).length">
                        <v-row>
                          <v-col class="pt-6 pb-0 px-0 ml-n1" cols="12">
                            <v-toolbar color="transparent" flat height="16">
                              <v-toolbar-title class="text--secondary body-2 font-weight-medium">Complementos</v-toolbar-title>
                            </v-toolbar>
                          </v-col>
                        </v-row>
                        <v-row align="center" justify="space-between" v-for="sa in subscription.addons.filter(sa => sa.selected)" :key="sa.id">
                          <v-col>
                            <span class="d-block mb-1 body-2 text--secondary font-weight-medium">
                              {{sa.name}}
                              <v-tooltip max-width="320px" bottom transition="scale-transition">
                                <template v-slot:activator="{on}">
                                  <v-icon v-on="on" small color="secondary">mdi-information</v-icon>
                                </template>
                                <span class="d-block text--primary">{{sa.description}}</span>
                              </v-tooltip>
                            </span>
                            <span class="d-block">
                              <span class="body-2">{{sa.addon.amount | currency(activeCurrency.id)}}</span>
                              <span class="ml-1 caption text--primary">{{activeCurrency.id}}</span>
                              <span class="ml-1 caption text--primary">
                                <template v-if="sa.addon.apply_scheme === 'one_time'">el primer {{activeFrequency | frequency('unit')}}</template>
                                <template v-else>{{activeFrequency | frequency}} por {{sa.addon.duration_times}} {{activeFrequency | frequency('plural')}}</template>
                              </span>
                            </span>
                          </v-col>
                          <v-col cols="2" class="text-right">
                            <template v-if="sa.allow_quantity">
                              <span class="caption text--secondary">Cant. {{sa.quantity | number}}</span>
                            </template>
                          </v-col>
                          <v-col lg="4" sm="5" class="text-right">
                            <span class="body-2 text--secondary font-weight-medium">{{sa.addon.amount * sa.quantity | currency(activeCurrency.id)}}</span>
                            <span class="ml-1 caption text--primary">{{activeCurrency.id}}</span>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-dialog>
                <!-- end checkout detail -->

              </v-col>
            </v-row>
            <v-row class="py-3 px-6">
              <v-toolbar class="d-block" color="transparent" flat height="55">
                <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Método de pago</v-toolbar-title>
              </v-toolbar>
              <template v-if="signupsDetail.source && signupsDetail.source.status === 'chargeable'">
                <v-col cols="12">
                  <v-sheet class="d-block py-3 px-9" color="background" rounded style="max-width:356px; max-width:428px">
                    <v-row align="end" justify="space-between">
                      <span class="mt-4 subtitle-1 text--secondary font-weight-medium">••• ••• ••• {{signupsDetail.source.card.last4}}</span>
                    </v-row>
                    <v-row align="end">
                      <v-col class="px-0" cols="6">
                        <span class="d-block caption">Fecha de expiración</span>
                        <span class="subtitle-1 text--secondary font-weight-medium">••/••</span>
                      </v-col>
                      <v-col class="px-0" cols="6">
                        <span class="d-block caption">CVV</span>
                        <span class="subtitle-1 text--secondary font-weight-medium">•••</span>
                      </v-col>
                    </v-row>
                    <v-row align="end" justify="space-between">
                      <v-col class="px-0" cols="6">
                        <span class="my-1 py-0 secondary secondary--text">•••••••••••••••••</span>
                        <br />
                        <span class="my-1 py-0 secondary secondary--text">•••••••••••••••••••••••</span>
                      </v-col>
                      <v-col class="px-0 text-right" cols="6">
                        <span><img :src="require(`@/assets/appAsset-brand--${signupsDetail.source.card.brand}.svg`)" :alt="signupsDetail.source.card.brand" height="24"></span>
                      </v-col>
                    </v-row>
                  </v-sheet>
                </v-col>
              </template>
              <template v-else>
                <template v-if="instance.require_source">
                  <v-col cols="12" v-if="!instance.test_mode && !instance.gateways.length">
                    <v-alert border="top" type="warning" :icon="false" text>
                      <span class="body-2 font-weight-medium deep-orange--text text--darken-4">No tenemos como procesar tu tarjeta. Contáctanos para resolver el problema.</span>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" v-if="activeGateway.id && activeGateway.flow === 'none'">
                    <template v-if="activeGateway.id === 'stripe'">
                      <p-gateway-stripe ref="stripe" :test-mode="instance.test_mode" :gateway="activeGateway" />
                    </template>
                  </v-col>
                  <v-col cols="12" v-else-if="activeGateway && activeGateway.flow === 'redirect'">
                    <template v-if="!waitingCard">
                      <v-btn class="ml-1" v-if="!signupsDetail.source || !signupsDetail.source.id" @click="createSignup" x-large outlined color="#D8E0EA">
                        <img :src="require(`@/assets/appAsset-brand--${activeGateway.id}Logo.svg`)" :alt="activeGateway.id" height="56">
                      </v-btn>
                      <v-btn class="ml-1" v-else @click="trySourceInscription" x-large outlined color="#D8E0EA">
                        <img :src="require(`@/assets/appAsset-brand--${activeGateway.id}Logo.svg`)" :alt="activeGateway.id" height="56">
                      </v-btn>
                    </template>
                    <v-row class="mx-0" align="center" justify="start" v-else>
                      <v-col>
                        <v-progress-circular indeterminate :color="color"></v-progress-circular>
                        <span class="d-block mt-3">Esperando registro de tarjeta en {{activeGateway.id}}...</span>
                        <v-btn @click="retrieveSignup" text :color="color">Actualizar</v-btn>
                      </v-col>
                    </v-row>
                    <v-alert class="mt-6 mb-0" text dense border="left" type="error" icon="mdi-alert" v-if="!waitingCard && $v.signup.source.$error">Selecciona un método de pago para registrar tu tarjeta.</v-alert>
                  </v-col>
                </template>
                <v-col cols="12" v-else>
                  <v-sheet class="pa-6" color="background" rounded>
                    <span class="text--secondary">Te enviaremos la factura junto a un link de pago en cada periodo de facturación.</span>
                  </v-sheet>
                </v-col>
              </template>
            </v-row>
            <v-row align="center" justify="space-between" class="py-2 px-10">
              <v-btn v-if="displayReturn" @click="step=2" height="38" outlined :ripple="false"><v-icon left small>mdi-arrow-left</v-icon>Facturación</v-btn>
              <v-spacer />
              <v-btn class="ml-6" @click="(!instance.require_source || (instance.require_source && activeGateway.flow === 'none')) ? createSignup() : createSubscription()" :loading="loaderCreateSignup || loaderCreateSubscription" :color="color" dark height="38" :ripple="false">Finalizar</v-btn>
            </v-row>
          </v-stepper-content>
          <!-- end payment -->

        </v-stepper-items>
      </v-stepper>
    </v-col>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { email, maxLength, numeric, required, requiredIf } from 'vuelidate/lib/validators'
import { rutValidator } from 'vue-dni'

import baseGateways from '../../collections/gateways'
import PGatewayStripe from '../../components/PGatewayStripe.vue'
import PSubscriptionLineDetail from '../../components/signup_forms/PSubscriptionLineDetail.vue'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import WidgetViewMixin from '@/mixins/WidgetViewMixin'

const gateways = baseGateways.filter(g => g.allowReusableUse)

export default {
  name: 'signup-form',
  mixins: [
    GenericViewMixin,
    WidgetViewMixin
  ],
  components: {
    PGatewayStripe,
    PSubscriptionLineDetail
  },
  data: () => ({
    activeFrequency: '',
    activeAddonToQuantityIndex: 0,
    activePlanToQuantityIndex: 0,
    aggregations: {
      addons: [],
      discount: 0,
      lines: [],
      totals: []
    },
    options: {
      backgroundColor: {
        type: 'string',
        value: 'transparent'
      },
      selectedPlans: {
        type: 'object',
        value: [
          { custom_id: '', quantity: 0 }
        ]
      }
    },
    context: {
      customer: {
        type: 'object',
        value: {},
        allowed_attrs: ['address', 'country', 'email', 'name', 'tax_id']
      },
      metadata: {
        type: 'object',
        value: {}
      }
    },
    dialogAddonQuantity: false,
    dialogPlanQuantity: false,
    dialogCheckoutDetail: false,
    displayReturn: true,
    activeGateway: {},
    gateways: gateways,
    gatewaysConnected: [],
    loaderCreateSignup: false,
    loaderCreateSubscription: false,
    retrieveSignupLoader: false,
    mainLoader: false,
    signup: {
      customer: {
        address: '',
        country: null,
        currency: '',
        email: '',
        name: '',
        state: null,
        tax_id: '',
        tax_settings: {
          cl_activity_description: ''
        }
      },
      source: {
        gateway: '',
        return_url: null,
        token: ''
      }
    },
    statesList: [],
    step: 1,
    subscription: {
      addons: [],
      coupon: null,
      document: null,
      lines: []
    },
    instantError: null,
    instantErrorReasons: [
      { id: 'biller_issue', label: 'No se ha logrado procesar la facturación. Contáctanos para resolver el problema.' },
      { id: 'gateway_issue', label: 'No se ha logrado procesar el pago. Inténtalo nuevamente con una nueva tarjeta.' },
      { id: 'processing_issue', label: 'No se ha logrado procesar la suscripción. Vuelve a intentarlo o contactáctanos para resolver el problema.' }
    ],
    waitingCard: false
  }),
  computed: {
    ...mapState({
      instance: state => state.apps.formsDetail,
      customersDetail: state => state.apps.customersDetail,
      // temp persistent state
      selectedAddons: state => state.apps.selectedAddons,
      selectedPlans: state => state.apps.selectedPlans,
      // end temp persistent state
      signupsDetail: state => state.apps.signupsDetail,
      sourcesDetail: state => state.apps.sourcesDetail,
      subscriptionsDetail: state => state.apps.subscriptionsDetail
    }),
    aggregationsSource () {
      return {
        addons: this.subscription.addons.filter(sa => sa.selected).map(sa => {
          return {
            exempt: sa.addon.exempt ? sa.addon.amount : 0,
            amount: sa.addon.exempt ? 0 : sa.addon.amount,
            quantity: sa.quantity
          }
        }),
        lines: this.subscription.lines.filter(sl => sl.selected).map(sl => {
          return {
            exempt: sl.plan.exempt ? sl.plan.amount : 0,
            amount: sl.plan.exempt ? 0 : sl.plan.amount,
            quantity: sl.quantity
          }
        }),
        discount_scheme: this.subscription.coupon ? this.subscription.coupon.discount_scheme : '%',
        discount: this.subscription.coupon ? this.subscription.coupon.amount : 0
      }
    },
    availableFrequencies () {
      if (!this.instance.available_plans) {
        return []
      }

      return this.frequencies.filter(f => this.instance.available_plans.some(ap => ap.plan.frequency === f.id))
    },
    color () {
      return this.instance.account && this.instance.account.color_background ? this.instance.account.color_background : 'primary'
    },
    activeCurrency () {
      return this.currencies.find(c => c.id === this.instance.currency)
    },
    activeDocument () {
      return this.instance.available_documents.find(ad => ad.document.id === this.subscription.document)
    },
    activeTierIndex () {
      const i = this.activePlanToQuantityIndex
      const tiers = this.subscription.lines[i].plan.tiers
      const quantity = this.subscription.lines[i].quantity

      let tierIndex = tiers.findIndex(t => t.quantity_to && t.quantity_from <= quantity && t.quantity_to >= quantity)

      // if no tier, default is last one
      if (tierIndex === -1) {
        tierIndex = tiers.findIndex((t, i) => i === tiers.length - 1)
      }

      return tierIndex
    },
    customLabels () {
      return this.instance.custom_labels.reduce((obj, item) => Object.assign(obj, { [item.field_name]: item.label }), {})
    },
    itemsDocumentSelect () {
      if (!this.selectedCountry) {
        return []
      }
      const country = this.baseCountries.find(c => c.id === this.selectedCountry.id)
      return this.instance.available_documents.filter(d => country.invoiceTypes.includes(d.document.id))
    },
    selectedCountry () {
      return this.instance.available_countries.find(ac => ac.id === this.signup.customer.country)
    },
    formHasMandatoryAddons () {
      return this.instance.available_addons.some(aa => aa.is_mandatory)
    }
  },
  watch: {
    aggregationsSource: {
      handler () {
        this.setAggregations()
      },
      deep: true
    },
    'signup.customer.country' () {
      this.statesList = this.instance.available_countries.find(ac => ac.id === this.signup.customer.country).states
      this.signup.customer.state = ''
      this.signup.customer.tax_id = ''
      this.subscription.document = ''

      if (this.itemsDocumentSelect.length === 1) {
        this.subscription.document = this.itemsDocumentSelect[0].document.id
      }
    },
    'signup.customer.currency' () {
      this.setGateway()
    },
    'subscription.document' () {
      this.signup.customer.currency = this.activeDocument.currency
      this.setAggregations()
    },
    step () {
      this.error = null
    }
  },
  created () {
    // check if is loaded in iframe
    if (window.self === window.top) {
      this.error = 'void'
      return false
    }

    this.mainLoader = true

    this.$store.dispatch('apps/RETRIEVE', {
      resource: 'publishable/signup_forms/forms',
      id: this.$route.params.id
    })
    .then(() => {
      if (this.instance.available_countries.length === 1) {
        this.signup.customer.country = this.instance.available_countries[0].id
      }
    })
    .then(() => {
      if (this.instance.available_documents.length === 1) {
        const ad = this.instance.available_documents[0]
        this.subscription.document = ad.document.id
      }
    })
    .then(() => {
      this.subscription.addons = this.instance.available_addons.map(aa => {
        return {
          ...aa,
          available_addon: aa.id,
          quantity: aa.min_quantity,
          selected: aa.is_mandatory
        }
      })
    })
    .then(() => {
      this.subscription.lines = this.instance.available_plans.map(ap => {
        return {
          ...ap,
          available_plan: ap.id,
          quantity: ap.min_quantity,
          selected: ap.is_mandatory,
          subtotal: ap.plan.amount * ap.min_quantity
        }
      })

      if (this.instance.available_plans.length === 1) {
        this.subscription.lines[0].selected = true

        if (!this.instance.available_addons.length) {
          this.step = 2
        }
      }
    })
    .then(() => {
      this.gatewaysConnected = this.instance.gateways.map(g => g.gateway)
      this.setGateway()
    })
    .then(() => {
      /**
       * add return_url to signup source
       */
      if (this.$route.query.referrer) {
        this.signup.source.return_url = this.$route.query.referrer
      }
    })
    .then(() => {
      /**
       * Reset store (localstorage) if the form is in test mode
       */
      if (this.instance.test_mode) {
        this.$store.dispatch('apps/RESET_SIGNUP_FORMS')
        return
      }

      /**
       * If the query psourcestatus exists, it retrieves
       * values from localstorage, instead must be restored
       * (remove from localstorage) as long as the use
       * of caching is disabled in form configuration.
       */
      if (this.$route.query.psourcestatus) {
        this.restoreFromStore()
        return
      }

      if (this.instance.use_cache) {
        this.restoreFromStore()
        return
      }

      this.$store.dispatch('apps/RESET_SIGNUP_FORMS')
    })
    .then(() => {
      this.activeFrequency = this.availableFrequencies[0].id
    })
    .finally(() => {
      // after load main data run init
      this.init()

      // hide loader
      this.mainLoader = false
    })
  },
  methods: {
    init () {
      /**
       * Setup context data and options injected to widget
       */

      // assign metadata context data
      this.subscription.metadata = this.context.metadata.value

      // assign customer context data
      if (this.signupsDetail.source) {
        return
      }

      this.context.customer.allowed_attrs.forEach(k => {
        if (this.context.customer.value[k]) {
          this.signup.customer[k] = this.context.customer.value[k]
        }
      })

      // validate scheme of options.selectedPlans data using Vuelidate
      this.$v.options.selectedPlans.value.$touch()

      if (this.$v.options.selectedPlans.value.$invalid) {
        console.info('malformed data in piriodSettings.options.selectedPlans')
        return
      }

      this.options.selectedPlans.value.forEach((sp, index) => {
        const i = this.subscription.lines.findIndex(line => line.custom_id === sp.custom_id)

        // set default frequency
        if (index === 0) {
          this.activeFrequency = this.subscription.lines[i].plan.frequency
        }

        if (i !== parseInt('-1') && this.subscription.lines[i].plan.frequency === this.activeFrequency) {
          this.subscription.lines[i].selected = true

          if (sp.quantity && this.subscription.lines[i].allow_quantity && sp.quantity >= this.subscription.lines[i].min_quantity) {
            this.subscription.lines[i].quantity = sp.quantity
          }
        }
      })
    },
    async createSignup () {
      this.loaderCreateSignup = true
      this.displayReturn = false

      if (this.instance.require_source && this.activeGateway.flow === 'none') {
        const token = await this.$refs[this.activeGateway.id].send()
        this.signup.source.token = token
      }

      this.$v.signup.$touch()

      if (this.$v.signup.$invalid) {
        this.loaderCreateSignup = false
        this.displayReturn = true
        return false
      }

      const payload = Object.assign({}, this.signup, {
        form: this.instance.publishable_key
      })

      if (!this.instance.require_source) {
        delete payload.source
      }

      this.$store.dispatch('apps/CREATE', {
        resource: 'publishable/signup_forms/signups',
        payload: payload
      })
      .then(() => {
        if (this.instance.require_source && this.activeGateway.flow === 'redirect') {
          /**
           * Save temporally selected products (addons & plans) to restore
           * this on gateway return redirection. Clean this redirection after.
           */
          this.$store.dispatch('apps/SET_SELECTED_PRODUCTS', {
            selectedAddons: this.subscription.addons.filter(a => a.selected),
            selectedPlans: this.subscription.lines.filter(p => p.selected)
          })

          this.trySourceInscription()
        } else {
          this.createSubscription()
        }
      })
      .catch((error) => {
        this.displayReturn = true

        // evaluate posible existing customer error
        if (error.response.status === 400) {
          const customerExists = 'Customer is already subscribed.'
          const nonFieldErrors = error.response.data.customer?.non_field_errors

          if (nonFieldErrors && nonFieldErrors.length && nonFieldErrors[0] === customerExists) {
            this.error = 'Ya tienes una suscripción.'
          } else {
            this.error = this.parseErrors(error.response.data)
          }
        } else {
          this.error = 'Error desconocido, vuelve a intentarlo más tarde.'
        }
      })
      .finally(() => {
        this.loaderCreateSignup = false
      })
    },
    createSubscription () {
      if (this.instance.require_source && this.signupsDetail.source?.status !== 'chargeable') {
        return false
      }

      this.$v.subscription.$touch()
      if (this.$v.subscription.$invalid) {
        return false
      }

      this.loaderCreateSubscription = true

      const payload = {
        document: this.subscription.document,
        lines: this.subscription.lines.filter(sl => sl.selected).map(sl => {
          return {
            available_plan: sl.id,
            quantity: sl.quantity
          }
        }),
        signup: this.signupsDetail.publishable_key,
        metadata: this.subscription.metadata
      }

      if (this.subscription.addons.length) {
        payload.addons = this.subscription.addons.filter(sa => sa.selected).map(sa => {
          return {
            available_addon: sa.id,
            quantity: sa.quantity
          }
        })
      }

      this.$store.dispatch('apps/CREATE', {
        resource: 'publishable/signup_forms/subscriptions',
        payload: payload
      })
      .then(() => {
        // emit message event
        parent.postMessage({
          event: 'piriod_signup_form_subscription',
          status: 'succeeded'
        }, '*')

        // redirect and clean store
        if (this.instance.redirect_url) {
          this.onRedirect(this.instance.redirect_url)
        } else {
          this.$router.push({
            name: 'SignupFormsSuccess',
            params: {
              id: this.signupsDetail.publishable_key
            }
          })
        }

        this.$store.dispatch('apps/RESET_SIGNUP_FORMS')
      })
      .catch((error) => {
        if (this.instance.instant) {
          /**
           * Reload signup to get source real status
           * and show process error in payment step
           */
          this.retrieveSignup()

          const ie = this.instantErrorReasons.find(r => r.id === error?.response?.data?.non_field_errors)

          /**
           * Assing reason label to show error to user.
           * The error is expected to be correctly
           * typed from the API. Other types of errors
           * are not supported.
           */
          this.instantError = ie?.label
        }
      })
      .finally(() => {
        this.loaderCreateSubscription = false
      })
    },
    async retrieveSignup () {
      this.retrieveSignupLoader = true

      try {
        await this.$store.dispatch('apps/RETRIEVE', {
          resource: 'publishable/signup_forms/signups',
          id: this.signupsDetail.publishable_key,
          loader: false
        })

        if (this.signupsDetail.source && this.signupsDetail.source.status === 'chargeable') {
          this.waitingCard = false
          this.displayReturn = false
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.retrieveSignupLoader = false
      }
    },
    toCustomerStep () {
      this.$v.subscription.lines.$touch()

      if (this.$v.subscription.lines.$invalid) {
        return false
      }

      this.step = 2
    },
    toPaymentStep () {
      this.$v.signup.customer.$touch()
      this.$v.subscription.$touch()

      if (this.$v.signup.customer.$invalid || this.$v.subscription.$invalid) {
        return false
      }

      this.step = 3
    },
    cleanSubscriptionLines (line) {
      if (!this.instance.allow_multiple_plans) {
        this.subscription.lines.forEach((sl, i) => {
          if (sl.available_plan !== line.available_plan) {
            this.subscription.lines[i].selected = false
          }
        })
      } else {
        this.subscription.lines.forEach((sl, i) => {
          if (sl.plan.frequency !== line.plan.frequency) {
            this.subscription.lines[i].selected = false
          }
        })
      }
    },
    trySourceInscription () {
      this.waitingCard = true

      this.onRedirect(`${process.env.VUE_APP_API_BASE}/sources/${this.signupsDetail.source.id}/authorize/`)
    },
    setAggregations () {
      if (!this.activeCurrency || !this.activeDocument) {
        return false
      }

      this.aggregations.addons = []
      this.aggregations.lines = []

      const hasTaxes = this.activeDocument.document.has_taxes
      const taxRate = (this.activeDocument.document.tax / 100)
      const round = (this.activeCurrency.format && this.activeCurrency.format.precision === 0)

      const totals = [
        {
          amount: 0,
          endBillingCycle: null,
          exempt: 0,
          startBillingCycle: null,
          subtotal: 0,
          tax: 0,
          total: 0
        }
      ]

      // Computed plans subtotals
      this.subscription.lines.filter(sl => sl.selected).forEach((sl, i) => {
        const lineIndex = this.subscription.lines.findIndex(l => l.id === sl.id)
        let lineSubtotal = sl.plan.amount * sl.quantity

        if (sl.plan.tiers.length) {
          let tier = sl.plan.tiers.find(t => t.quantity_to && t.quantity_from <= sl.quantity && t.quantity_to >= sl.quantity)

          // if no tier, default is last one
          if (!tier) {
            tier = sl.plan.tiers.find((t, i) => i === sl.plan.tiers.length - 1)
          }

          lineSubtotal = sl.plan.tiers_mode === 'unit' ? tier.amount * sl.quantity : tier.amount
        }

        this.subscription.lines[lineIndex].subtotal = lineSubtotal
        this.aggregations.lines.push({ subtotal: this.formatNumber(lineSubtotal, round) })

        if (sl.plan.exempt || !hasTaxes) {
          totals[0].exempt += lineSubtotal
        } else {
          totals[0].amount += lineSubtotal
        }
      })

      /**
       * Computed addons subtotals. The first step is define duration times and push
       * a new total object to totals by each duration time addon. After each addon
       * sum subtotal to total object according with duration time. This way allow
       * obtain distinct duration time total based.
       */
      const durationTimes = this.subscription.addons.filter(a => a.selected)
      .map(a => {
        if (a.addon.apply_scheme === 'one_time') {
          return 1
        }
        return a.addon.duration_times
      })
      .filter((v, i, o) => o.indexOf(v) === i)
      .sort((a, b) => a - b)

      durationTimes.forEach((d, i) => {
        const startBillingCycle = i === 0 ? 1 : durationTimes[i - 1] + 1

        totals.push({
          amount: 0,
          endBillingCycle: d,
          exempt: 0,
          startBillingCycle: startBillingCycle,
          subtotal: 0,
          tax: 0,
          total: 0
        })
      })

      this.subscription.addons.filter(a => a.selected).forEach(a => {
        const endBillingCycle = a.addon.apply_scheme === 'one_time' ? 1 : a.addon.duration_times
        const i = totals.findIndex(t => t.endBillingCycle === endBillingCycle)

        // set value
        totals[i].amount += this.formatNumber(a.addon.amount * a.quantity, round)
        this.aggregations.addons.push({ subtotal: totals[i].amount })
      })

      // computed totals tax & total
      totals.forEach((t, i) => {
        /**
         * Sum plan values to another total objects values
         */
        if (i > 0) {
          const baseTotal = totals[0]
          totals[i].amount += baseTotal.subtotal
          totals[i].exempt += baseTotal.exempt
        }

        totals[i].subtotal = this.formatNumber(t.amount + t.exempt, round)
        totals[i].tax = this.formatNumber(hasTaxes ? (t.amount * taxRate) : 0, round)
        totals[i].total = this.formatNumber(t.subtotal + t.tax, round)
      })

      // first total to last
      totals.push(totals.shift())

      this.aggregations.totals = totals
    },
    setGateway () {
      if (this.instance.test_mode) {
        this.activeGateway = this.gateways.find(g => g.id === 'stripe')
        this.signup.source.gateway = this.activeGateway.id
      } else {
        this.activeGateway = this.gateways.find((g) => {
          if (this.gatewaysConnected.includes(g.id) && g.currencies.includes(this.signup.customer.currency)) {
            return g
          }
        })

        if (!this.activeGateway) {
          this.activeGateway = {}
        }

        if (this.activeGateway.id) {
          const g = this.instance.gateways.find(g => g.gateway === this.activeGateway.id)
          this.activeGateway.credentials = {
            publicKey: g.public_key
          }
        }

        this.signup.source.gateway = this.activeGateway.id
      }
    },
    restoreFromStore (displayReturn = false) {
      /**
       * Restore data from state (localstorage) if signup exists
       * @param displayReturn Boolean sets if Prev button should be shown in stepper
       */

      // check if signup exists
      if (!this.signupsDetail.publishable_key) {
        return
      }

      this.retrieveSignup().then(() => {
        /**
         * set selected plans
         * TODO: if signup form has changes while user registers his card
         * TODO: it's impossible to restore plans because they change their
         * TODO: ID at each update
         */
        this.selectedPlans.forEach(sp => {
          const i = this.subscription.lines.findIndex(l => l.id === sp.id)

          if (i !== parseInt('-1')) {
            this.subscription.lines[i].selected = true
            this.subscription.lines[i].quantity = sp.quantity
          }
        })

        // set selected addons
        this.selectedAddons.forEach(sa => {
          const i = this.subscription.addons.findIndex(a => a.id === sa.id)

          this.subscription.addons[i].selected = true
          this.subscription.addons[i].quantity = sa.quantity
        })

        // set customer data
        Object.keys(this.signup.customer).forEach(k => {
          this.signup.customer[k] = this.signupsDetail.customer[k]
        })

        // lock prev step button & go to payment step
        this.displayReturn = displayReturn
        this.step = 3
      })
    }
  },
  validations: {
    signup: {
      customer: {
        name: {
          required,
          maxLength: maxLength(100)
        },
        country: {
          required
        },
        state: {
          required
        },
        address: {
          required,
          maxLength: maxLength(64)
        },
        tax_id: {
          required: requiredIf(function () {
            return this.selectedCountry ? this.selectedCountry.has_regulation : false
          }),
          taxId: function (val) {
            if (this.signup.customer.country === 'CL') {
              return rutValidator(val)
            }
            return true
          },
          maxLength: maxLength(32)
        },
        tax_settings: {
          cl_activity_description: {
            required: requiredIf(function () {
              return this.signup.customer.country === 'CL'
            }),
            maxLength: maxLength(64)
          }
        },
        email: {
          required,
          email,
          maxLength: maxLength(254)
        }
      },
      source: {
        gateway: {
          required: requiredIf(function () {
            if (this.instance.require_source) {
              return true
            }
            return false
          })
        },
        token: {
          required: requiredIf(function () {
            if (this.instance.require_source && this.activeGateway.flow === 'none') {
              return true
            }
            return false
          })
        }
      }
    },
    subscription: {
      addons: {
        $each: {
          quantity: {
            required,
            minValue: function (value, addon) {
              if (value < addon.min_quantity) {
                return false
              }
              return true
            }
          }
        }
      },
      document: {
        required
      },
      lines: {
        minLength: function () {
          if (this.subscription.lines.filter(sl => sl.selected).length) {
            return true
          }
          return false
        },
        $each: {
          quantity: {
            required,
            minValue: function (value, line) {
              if (value < line.min_quantity) {
                return false
              }
              return true
            }
          }
        }
      }
    },
    options: {
      selectedPlans: {
        value: {
          $each: {
            custom_id: {
              required
            },
            quantity: {
              numeric
            }
          }
        }
      }
    }
  }
}
</script>

<style>
  .v-stepper {
    background: transparent !important;
  }
</style>